import React, { useContext, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2 } from "lucide-react";
import withholdingTaxAPI from "../../api/withholdingTaxAPI";
import Button from "../Button";
import { AuthContext } from "../../context/AuthContext";
import notify from "../../lib/notify";
import useGuardedMutation from "../../hooks/useGuardedMutation";
import { GlPreviewModal } from "./glUi";

// Payment journal entry of a FILED return version (G2): "Preview & post
// payment" → the shared preview modal → confirm; once posted a badge links
// the JE, and an admin can reverse it (reason required). The filed version is
// the latest one with status 'filed' (an amendment draft may be open on top).

const ReverseModal = ({ onCancel, onConfirm, loading }) => {
  const [reason, setReason] = useState("");
  return createPortal(
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-[9999] p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="reverse-payment-title" className="bg-white rounded-xl border border-ink-line shadow-card-hover p-6 w-full max-w-md">
        <h2 id="reverse-payment-title" className="text-lg font-medium text-ink">Reverse payment entry?</h2>
        <p className="text-sm text-ink-body mt-2">This posts a reversal of the payment entry. The return stays filed; the payment can be posted again afterwards.</p>
        <label className="block text-sm text-ink-body mt-3">Reason
          <textarea aria-label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            className="mt-1 w-full border border-ink-line rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500" />
        </label>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onCancel} disabled={loading}>Cancel</Button>
          <Button variant="danger" onClick={() => onConfirm(reason.trim())} loading={loading} disabled={reason.trim().length < 3}>Reverse</Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const ReturnPaymentGl = ({ client, form, view, onChanged, onOpenPostingAccounts }) => {
  const auth = useContext(AuthContext);
  const isAdmin = auth?.user?.role === "admin";
  const [previewing, setPreviewing] = useState(false);
  const [reversing, setReversing] = useState(false);
  const clientId = client?._id;

  const filed = [...(view?.versions || [])].reverse().find((v) => v.status === "filed");

  const reverse = async (reason) => {
    try {
      await withholdingTaxAPI.reverseReturnPayment(clientId, form, filed._id, reason);
      notify.success("Reversed", "The payment entry was reversed.");
      setReversing(false);
      await onChanged?.();
    } catch (e) {
      notify.error("Couldn't reverse", e.message);
    }
  };
  const { mutate: runReverse, isPending: reverseBusy } = useGuardedMutation(reverse);

  if (!filed) return null;
  const forAmendment = filed._id !== view.returnId ? ` (filed version${filed.isAmended ? `, amendment ${filed.amendmentSeq}` : ""})` : "";

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="payment-gl">
      {filed.paymentGlStatus === "posted" ? (
        <>
          <a href={`/journal-entry/${filed.paymentJournalEntryId}`} data-testid="payment-posted-badge"
            className="text-xs px-2 py-0.5 rounded-md bg-success-50 text-success-700 inline-flex items-center gap-1 hover:underline">
            <CheckCircle2 className="w-3 h-3" /> Payment posted{forAmendment}
          </a>
          {isAdmin && <Button variant="dangerOutline" size="sm" onClick={() => setReversing(true)}>Reverse payment entry</Button>}
        </>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setPreviewing(true)} disabled={filed.paymentGlStatus === "posting"}>
          {filed.paymentGlStatus === "posting" ? "Posting payment…" : `Preview & post payment${forAmendment}`}
        </Button>
      )}

      {previewing && (
        <GlPreviewModal
          client={client}
          title="Preview payment entry"
          previewRequest={() => withholdingTaxAPI.previewReturnPayment(clientId, form, filed._id)}
          postRequest={() => withholdingTaxAPI.postReturnPayment(clientId, form, filed._id)}
          onClose={() => setPreviewing(false)}
          onPosted={() => onChanged?.()}
          onOpenPostingAccounts={onOpenPostingAccounts && (() => { setPreviewing(false); onOpenPostingAccounts(); })}
        />
      )}
      {reversing && <ReverseModal onCancel={() => setReversing(false)} onConfirm={(r) => runReverse(r)} loading={reverseBusy} />}
    </div>
  );
};

export default ReturnPaymentGl;
